<template>
    <!-- Modal component with centralized state management through Pinia -->
    <Modal
        :is-open="showAddUserModal"
        @close="() => setShowAddUserModal(false)"
        title="Add user & wallet"
        icon="addUser"
    >
        <!-- Main content section -->
        <template #content>
            <div class="space-y-8">
                <!-- Email input section -->
                <div class="flex flex-col gap-6">
                    <p class="text-slate-800 dark:text-slate-200 text-sm font-normal">
                        Enter an email to invite someone to share their wallet.
                        You'll be able to manage their wallet while keeping
                        yours private.
                    </p>
                    <UiInput
                        type="email"
                        id="email"
                        name="email"
                        label="Email"
                        placeholder="Enter the user's email address"
                        v-model:inputValue="email"
                        :error="error"
                        autofocus
                        aria-describedby="email-error"
                    />
                    <div id="email-error" class="sr-only">
                        {{ error || '' }}
                    </div>
                </div>

                <!-- Feature highlights section -->
                <section
                    class="flex flex-col gap-3 rounded-lg outline outline-1 outline-offset-[-1px] outline-slate-200 dark:outline-slate-700 p-5"
                    aria-labelledby="features-heading"
                >
                    <h3
                        id="features-heading"
                        class="text-sm text-slate-800 dark:text-slate-100 flex gap-2"
                    >
                        <Icon icon="multipleMembers" />
                        My10x makes managing multiple users seamless:
                    </h3>
                    <ul class="text-slate-800 dark:text-slate-200 text-sm font-normal space-y-2">
                        <li class="flex items-center gap-2">
                            <div
                                class="w-5 h-5 overflow-hidden rounded-full bg-emerald-50 dark:bg-emerald-500/15 flex justify-center items-center"
                                aria-hidden="true"
                            >
                                <Icon icon="check" class="text-emerald-500" />
                            </div>
                            <div>Create a separate wallet for each user</div>
                        </li>

                        <li class="flex items-center gap-2">
                            <div
                                class="w-5 h-5 overflow-hidden rounded-full bg-emerald-50 dark:bg-emerald-500/15 flex justify-center items-center"
                                aria-hidden="true"
                            >
                                <Icon icon="check" class="text-emerald-500" />
                            </div>
                            <div>
                                Easily track each user's eligibility for new
                                products
                            </div>
                        </li>

                        <li class="flex items-center gap-2">
                            <div
                                class="w-5 h-5 overflow-hidden rounded-full bg-emerald-50 dark:bg-emerald-500/15 flex justify-center items-center"
                                aria-hidden="true"
                            >
                                <Icon icon="check" class="text-emerald-500" />
                            </div>
                            <div>
                                At a quick glance, know how many points each
                                user has across various loyalty programs
                            </div>
                        </li>

                        <li class="flex items-center gap-2">
                            <div
                                class="w-5 h-5 overflow-hidden rounded-full bg-emerald-50 dark:bg-emerald-500/15 flex justify-center items-center"
                                aria-hidden="true"
                            >
                                <Icon icon="check" class="text-emerald-500" />
                            </div>
                            <div>
                                There are no limits to how many wallets you can
                                manage
                            </div>
                        </li>
                    </ul>
                </section>
            </div>
        </template>

        <!-- Modal footer with action buttons -->
        <template #footer>
            <footer class="flex justify-end gap-2 items-center">
                <button
                    type="button"
                    class="btn-base btn-white"
                    @click.prevent="close"
                    id="hs-wallet-close-add-user-modal"
                >
                    Cancel
                </button>
                <ActionButton
                    @clicked="addUser"
                    label="Invite User"
                    :loading="addUserBtnAction"
                    classes="btn-base btn-dark"
                    buttonId="hs-wallet-add-user-submit"
                />
            </footer>
        </template>
    </Modal>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useWalletStore } from '@/store/wallet';
import Modal from '@/components/ui/modal/ui-modal.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { storeToRefs } from 'pinia';
import { notify } from 'notiwind';
import UiInput from '@/components/ui/inputs/ui-input.vue';
import ActionButton from '@/components/action-button.vue';

// Props
const props = defineProps({
    localwallet: {
        type: Number,
        default: 0,
    },
    refreshview: {
        type: Boolean,
        default: false,
    },
});

// Store
const walletStore = useWalletStore();
const { setShowAddUserModal, setNewInvitation } = walletStore;
const { showAddUserModal, wallet } = storeToRefs(walletStore);

// Reactive state
const addUserBtnAction = ref(false);
const email = ref('');
const error = ref(null);

// Methods
async function addUser() {
    error.value = null;
    addUserBtnAction.value = true;

    const re =
        /^([\w\+\-]+(?:\.[\w\+\-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/i;
    let validEmail = re.test(email.value);

    if (email.value.length === 0 || !validEmail) {
        error.value = 'Please enter a valid email.';
        notify(
            {
                group: 'error',
                title: 'Error',
                text: 'Please enter a valid email.',
            },
            4000
        );
        addUserBtnAction.value = false;
        return;
    } else {
        let data = {
            email: email.value,
        };

        const walletId = props.localwallet
            ? props.localwallet
            : wallet.value.id;

        try {
            const response = await ApiService.apiCall(
                `/wallets/${walletId}/users`,
                'post',
                data
            );

            const newInvitation = response.data.invitation;
            const successMessage = response.data.message;
            const isResend = response.data.is_resend;

            notify(
                {
                    group: 'success',
                    title: isResend ? 'New invitation sent' : 'Invitation sent',
                    text:
                        successMessage ||
                        (isResend
                            ? 'Another invitation has been sent to this user.'
                            : 'User invited.'),
                },
                4000
            );

            if (import.meta.env.VITE_APP_ENV === 'production') {
                woopra.track('wallet-user-added-my10x', {
                    email: data.email,
                    wallet_id: walletId,
                });
            }

            if (newInvitation) {
                setNewInvitation(newInvitation);
            }

            email.value = '';
            error.value = null;
            addUserBtnAction.value = false;
            setShowAddUserModal(false);

            if (props.refreshview) {
                window.location.reload();
            }
        } catch (err) {
            if (err.response?.data?.errors?.email) {
                error.value = err.response.data.errors.email[0];
            } else if (err.response?.data?.message) {
                error.value = err.response.data.message;
            } else {
                error.value = 'An error occurred while sending the invitation.';
            }

            notify(
                {
                    group: 'error',
                    title: 'Error',
                    text: error.value,
                },
                4000
            );

            addUserBtnAction.value = false;
        }
    }
}

function close() {
    email.value = '';
    error.value = null;
    setShowAddUserModal(false);
}

watch(
    () => email.value,
    () => {
        error.value = null;
    }
);
</script>
